import { useState, useEffect } from 'react';
import api from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../../components/ui/dialog';
import { AlertTriangle, UserCheck, Loader2, Calendar as CalIcon } from 'lucide-react';
import { formatDate, formatTime } from '../../lib/utils';
import { toast } from 'sonner';

/**
 * Sessions flagged `needs_reassignment: true` — either because their original
 * instructor was deleted, or (future) their instructor's leave was approved.
 * Admin picks a replacement from the instructor pool → backend swaps the
 * instructor + recreates the Zoom meeting on the new host's license.
 */
export default function ReassignmentQueuePage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [instructors, setInstructors] = useState([]);
  const [target, setTarget] = useState(null); // {session, newInstructorId}
  const [busy, setBusy] = useState(false);

  const fetchItems = () =>
    api.get('/api/admin/sessions/needs-reassignment').then((res) => setItems(res.data.sessions || []));

  useEffect(() => {
    Promise.all([
      fetchItems(),
      api.get('/api/users', { params: { role: 'instructor', limit: 100 } }).then((r) => setInstructors(r.data.users || [])),
    ]).finally(() => setLoading(false));
  }, []);

  const openReassign = (session) => setTarget({ session, newInstructorId: '' });

  const confirmReassign = async () => {
    if (!target?.newInstructorId) {
      toast.error('Pick a replacement instructor');
      return;
    }
    setBusy(true);
    try {
      await api.post(`/api/admin/sessions/${target.session.id}/reassign-instructor`, null, {
        params: { new_instructor_id: target.newInstructorId },
      });
      toast.success('Session reassigned. Zoom meeting rebuilt on the new instructor\'s license.');
      setTarget(null);
      await fetchItems();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to reassign');
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>;
  }

  return (
    <div className="space-y-6" data-testid="reassignment-queue-page">
      <div>
        <h1 className="font-heading text-3xl font-semibold tracking-tight flex items-center gap-3">
          <AlertTriangle className="h-7 w-7 text-amber-600" />
          Instructor Unavailable
        </h1>
        <p className="text-muted-foreground mt-1">
          Upcoming sessions whose original instructor was deleted or is on approved leave.
          Pick a replacement to restore the session — this also rebuilds the Zoom meeting on
          the new instructor's license and stamps an audit log.
        </p>
      </div>

      <Card className="rounded-2xl">
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <span>Pending actions</span>
            <Badge variant={items.length === 0 ? 'outline' : 'destructive'}>{items.length}</Badge>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {items.length === 0 ? (
            <div className="text-center py-10 text-muted-foreground text-sm">
              <UserCheck className="h-10 w-10 mx-auto mb-3 text-emerald-500" />
              All clear — no sessions need reassignment.
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Date &amp; time</TableHead>
                  <TableHead>Batch</TableHead>
                  <TableHead>Original instructor</TableHead>
                  <TableHead>Reason</TableHead>
                  <TableHead>Booked</TableHead>
                  <TableHead className="text-right">Action</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {items.map((s) => (
                  <TableRow key={s.id} data-testid={`reassign-row-${s.id}`}>
                    <TableCell className="font-mono text-xs">
                      <div>{formatDate(s.scheduled_date)}</div>
                      <div className="text-muted-foreground">{formatTime(s.scheduled_time)} · {s.duration_minutes}m</div>
                    </TableCell>
                    <TableCell>{s.batch_name || '—'}</TableCell>
                    <TableCell>
                      <span className="text-sm line-through text-muted-foreground">{s.original_instructor_name || 'Unknown'}</span>
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline" className="text-[10px] capitalize">{(s.reassignment_reason || '').replace(/_/g, ' ')}</Badge>
                    </TableCell>
                    <TableCell>{s.booked_learners_count} learner{s.booked_learners_count !== 1 ? 's' : ''}</TableCell>
                    <TableCell className="text-right">
                      <Button size="sm" onClick={() => openReassign(s)} data-testid={`reassign-btn-${s.id}`}>
                        Reassign
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Reassign modal */}
      <Dialog open={!!target} onOpenChange={(v) => !v && !busy && setTarget(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reassign session</DialogTitle>
            <DialogDescription>
              {target?.session && (
                <>
                  <span className="block font-medium text-foreground">{target.session.batch_name}</span>
                  <span>{formatDate(target.session.scheduled_date)} · {formatTime(target.session.scheduled_time)} · {target.session.duration_minutes} min</span>
                </>
              )}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <label className="text-sm font-medium">Pick a replacement instructor</label>
            <Select
              value={target?.newInstructorId || ''}
              onValueChange={(v) => setTarget((s) => ({ ...s, newInstructorId: v }))}
            >
              <SelectTrigger data-testid="reassign-instructor-select"><SelectValue placeholder="Choose instructor..." /></SelectTrigger>
              <SelectContent>
                {instructors.map((i) => (
                  <SelectItem key={i.id} value={i.id}>
                    {i.name} <span className="text-xs text-muted-foreground">· {i.email}</span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground">
              <CalIcon className="inline h-3 w-3 mr-1" />
              The Zoom meeting will be rebuilt on the new instructor's license.
              Booked learners will need the new join link — consider notifying them manually.
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setTarget(null)} disabled={busy}>Cancel</Button>
            <Button onClick={confirmReassign} disabled={busy} data-testid="confirm-reassign-btn">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Confirm'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
